<template>
	<v-form
		class="login-form"
		ref="form"
		v-model="valid"
		lazy-validation
		@submit.prevent="validateAndSubmit"
	>

		<div class="post-text__wrapper">
			<div class="d-flex flex-column">
				<div class="d-flex justify-space-between flex-wrap mt-5">
					<div style="flex-grow: 1;">
						<span class="post-text__field-title" >Select Category *</span>

						<v-skeleton-loader class="post-text__field-loader" type="card" v-if="isLoading">
						</v-skeleton-loader>

						<v-select
							v-else
							class="post-text__field-select mt-2"
							clearable
							:items="forumCategories"
							item-text="category_name"
							variant="solo"
							:menu-props="menuProps"
							return-object
							:rules="rules.required"
							v-model="selectedCategory"
							:disabled="isCategoryDisabled"
							append-icon="mdi-chevron-down"
							@change="onSelectChange"
						></v-select>
					</div>
				</div>

				<div class="d-flex justify-space-between flex-wrap align-center mb-3">
					<div style="flex-grow: 1;">
						<span class="post-text__field-title"
						style="font-size: 14px">Text *</span>

						<v-textarea
							class="post-text__field-input pt-0 mt-2"
							color="red"
							v-model="fields.forum_text"
							:error-messages="field_errors.required"
							:rules="rules.required"
							required
							rows="5"
							solo
						>
						</v-textarea>
						<p
							class="red--text text-center"
							v-for="error in errors"
							:key="error"
							style="width: 100%"
						>
							{{ error }}
						</p>
					</div>
				</div>
			</div>

			<div class="post-text__file-wrapper flex-column pt-8 align-start mb-3">
				<div class="post-text__file-container">

					<v-skeleton-loader
						v-if="this.isFileLoading"
						style="width: 100%;"
						type="card"
						class="post-text__file-container-loader"
					></v-skeleton-loader>

					<div
						class="pa-0 post-card__thumbnail-outer"
						v-if="this.fields.upload_ids.length > 0 && (this.postFile || this.editableForum && this.editableForum.uploads[0].url)"
					>
						<img
							class="post-card__thumbnail"
							:src="this.postFile ? this.postFile.url : this.editableForum.uploads[0].url"
							alt="forum image"
							loading="lazy"
						/>
					</div>

					<input
						type="file"
						id="file9"
						name="file"
						class="post-text__file-uploader"
						accept="image/png, image/gif, image/jpeg"
						@change="handleUploadFile"
						v-if="this.fields.upload_ids.length <= 0 && !this.isFileLoading"
					/>

					<div
						class="d-flex flex-column justify-center align-center post-text__file-container-loader"
						v-if="this.fields.upload_ids.length <= 0 && !this.isFileLoading"
					>
						<v-icon :size="50"> mdi-image </v-icon>
						<div class="post-text__file-container-title">
							Drag and Drop or
							<div class="post-text__file-container-link ml-1">Browse</div>
						</div>
						<div class="post-text__file-container-description">
							JPG, PNG. Max size 20 MB
						</div>
					</div>

				</div>

				<div v-if="this.fields.upload_ids.length > 0">
					<div
						class="d-flex align-center mb-1"
						style="cursor: pointer"
						@click="isViewConfirmDeleteImageButtons"
					>
						<Icon
							type="document"
							color="var(--accent-color)"
							:size="24"
							:pr="8"
						/>
						<div class="post-text__confirm-delete-text">Delete File</div>
					</div>

					<div v-if="showConfirmDeleteImageButtons">
						<div class="post-text__confirm-delete mb-3">
							Are you sure that you want to <br />delete the file?
						</div>
						<div>
							<v-btn
								class="post-text__confirm-delete elevation-0 mr-8"
								outlined
								@click="isViewConfirmDeleteImageButtons"
							>
								No
							</v-btn>
							<v-btn
								class="post-text__confirm-delete elevation-0"
								:loading="false"
								:disabled="false"
								@click="removeImage"
							>
								Yes
							</v-btn>
						</div>
					</div>
				</div>
			</div>
		</div>

		<div class="post-text__action-btn-wrapper">
			<v-btn 
				class="post-text__action-btn action-cancel elevation-0" 
				outlined 
				@click="closeModal"
			>
				Cancel
			</v-btn>
	
			<v-btn
				class="post-text__action-btn action-create elevation-0"
				:loading="isLoading || forumBeingEdited"
				:disabled="isSubmitBtnDisabled"
				@click="validateAndSubmit"
				:style="{borderColor: isSubmitBtnDisabled ? 'rgba(0, 0, 0, 0.12)' : 'var(--button-main-background-color)'}"
			>
				{{ edit ? 'Save' : 'Create' }}
			</v-btn>
		</div>

		<p
			class="red--text mt-2 mb-0"
			v-for="error in this.postErrors"
			:key="error"
			style="width: 100%"
		>
			{{ error.message }}
		</p>
	</v-form>
</template>

<script>
import { ruleRequired } from '@/utils/rules'
import Form from '@/mixins/Form'
import { mapActions, mapGetters } from 'vuex'
import _ from 'lodash'

const DEBOUNCE_TIMEOUT = 500

export default {
	name: 'PostTextForm',
	mixins: [Form],

	props: {
		activeTab: {
			type: Number,
			default: 0,
		},
		categoryId: {
			type: Number,
			default: null
		},
		showForm: {
			type: Boolean,
			default: false
		},
		edit: {
			type: Boolean,
			default: false
		},
		editableForum: {
			type: Object,
			default: () => {}
		}
	},

	data: () => ({
		selectedCategory: {},
		fields: {
			category_id: 0,
			forum_text: '',
			upload_ids: []
		},

		menuProps: {
			// maxWidth: '250px',
			offsetY: true,
		},
		chips: [],
		showConfirmDeleteImageButtons: false,
		timer: 0,
	}),
	computed: {
		...mapGetters('settings', ['chaptersList', 'appCore', 'styles']),
		...mapGetters('forum', ['forumCategories', 'chosenCategory', 'isLoading', 'forumBeingEdited']),
		...mapGetters('post', ['isFileLoading', 'postFile', 'postErrors']),
		...mapGetters('tag', ['tags']),

		rules() {
			return {
				required: [(v) => ruleRequired(v) || 'Field is required.'],
			}
		},
		section() {
			const parsedMenu = JSON.parse(this.appCore.app_menu)
			return parsedMenu.find((item) => item.url.includes('threads'))
		},
		isCategoryDisabled() {
			if (!this.selectedCategory || !this.$props.categoryId) {
				return false
			}

			return this.selectedCategory.hasOwnProperty('category_id')
		},
		buttonColor() {
			return this.styles.button_main_background + '!important'
		},
		isSubmitBtnDisabled() {
			if (this.edit) {
				return this.editableForum.forum_text === this.fields.forum_text && 
				_.isEqual(this.editableForum.uploads, this.fields.upload_ids)
			}
			return false
		}
	},

	methods: {
		...mapActions('post', ['uploadFile']),
		...mapActions('forum', ['addPost', 'editForum']),
		...mapActions('tag', ['fetchTags']),

		async submitForm() {
			if (this.edit) {
				if (this.fields.upload_ids.length && typeof this.fields.upload_ids[0] !== 'number') {
					this.fields.upload_ids = this.fields.upload_ids.map(item => item.upload_id)
				}
				const response = await this.editForum({ id: this.editableForum.forum_id, data: this.fields })
				if (response) {
					this.closeModal()
				}
			} else {
				this.addPost(this.fields).then(() => {
					this.closeModal()
				})
			}
		},

		isViewConfirmDeleteImageButtons() {
			this.showConfirmDeleteImageButtons = !this.showConfirmDeleteImageButtons
		},

		onSelectChange(selectedValue) {
			if (selectedValue) {
				this.fields.category_id = _.toNumber(selectedValue.category_id)
			} else {
				this.fields.category_id = null
			}
		},

		remove(item) {
			this.chips.splice(this.chips.indexOf(item), 1)
		},

		handlerInputTag(item) {
			clearTimeout(this.timer)

			this.timer = setTimeout(() => {
				this.fetchTags({ search: item, filter: 'threads' })
			}, DEBOUNCE_TIMEOUT)
		},

		removeImage() {
			this.fields.upload_ids = []
			this.isViewConfirmDeleteImageButtons()
		},
		handlerAllowComments(value) {
			this.fields.allow_comments = _.toNumber(value)
		},

		closeModal() {
			this.$emit('closeModal')
			this.$refs.form.reset()
			this.removeImage()
		},

		handleUploadFile(e) {
			const file = e.target.files[0]
			this.uploadFile(file)
		},
		debug(values) {
			this.fields.tagNames = values.map((item) => item.tag)
		},
	},

	watch: {
		postFile() {
			this.fields.upload_ids = [...this.fields.upload_ids, this.postFile.upload_id]
		},

		chosenCategory: {
			handler() {
				this.selectedCategory = this.chosenCategory
				this.fields.category_id = this.chosenCategory?.category_id || 0
			},
			immediate: true
		}
	},

	created() {
		if (this.$props.categoryId) {
			this.fields.category_id = this.$props.categoryId
		}

		if (this.edit && this.editableForum) {
			this.selectedCategory = this.editableForum.category
			this.fields = {
				category_id: this.editableForum.category_id,
				forum_text: this.editableForum.forum_text,
				upload_ids: this.editableForum.uploads
			}
		}
	},
}
</script>

<style lang="scss">
.post-text {
	&__field-title {
		width: 37px;
		height: 17px;
		color: #697583 !important;
		line-height: 17.07px;
		font-size: 14px;
		font-family: Montserrat, sans-serif;
		font-weight: 400;
		letter-spacing: -0.34px;
	}

	&__wrapper {
		@media (max-width: 768px) {
			flex-direction: column;
		}
	}

	&__field-loader {
		width: 100%;
		height: 40px;
	}

	&__field-input {
		.menu__content {
			width: 200px !important;
		}
		.v-input__slot {
			margin-top: 0px;
			height: auto !important;
			min-height: 40px !important;
			box-shadow: none !important;
			border: 1px solid rgb(223, 223, 223);
		}
		i {
			color: #bbbbbb !important;
		}
		input {
			padding-bottom: 20px;
		}
		.v-select__selections {
			flex-wrap: nowrap;
			overflow: scroll;
			&::-webkit-scrollbar {
				display: none;
			}
		}
		.v-chip {
			min-width: fit-content;
		}
	}

	&__field-select {
		.v-input__slot:before {
			display: none;
		}
		padding: 0;
		margin: 0;
		.v-select__slot {
			height: 40px;
			border: 1px solid rgb(223, 223, 223);
			border-radius: 6px;
		}

		i {
			margin-top: 10px;
			margin-right: 10px;
			color: #bbbbbb !important;
		}
		button {
			margin-top: 10px;
		}
	}
	&__confirm-delete {
		color: rgb(74, 74, 74);
		font-size: 14px;
		font-family: Montserrat, sans-serif;
		font-weight: 500;
		&-text {
			color: var(--accent-color);
			font-size: 14px;
			font-family: Montserrat, sans-serif;
			font-weight: bold;
			text-decoration: underline;
			letter-spacing: -0.34px;
		}
	}
	&__file-wrapper {
		display: flex;
		flex-wrap: wrap;
		column-gap: 40px;
	}
	&__file-container {
		background-image: url("data:image/svg+xml,%3csvg width='100%25' height='100%25' xmlns='http://www.w3.org/2000/svg'%3e%3crect width='100%25' height='100%25' fill='none' rx='10' ry='10' stroke='%23A0A0A0FF' stroke-width='2' stroke-dasharray='6%2c 14' stroke-dashoffset='0' stroke-linecap='square'/%3e%3c/svg%3e");
		width: 100%;
		border: 1px;
		border-radius: 4px;
		cursor: pointer;
		margin-top: -60px;
		margin-bottom: 10px;

		@media (max-width: 768px) {
			width: 100%;
		}

		img {
			width: 100%;
			object-fit: contain;
			cursor: auto;
			border-radius: 6px;
		}

		input {
			width: 100%;
			height: 130px;
			position: absolute;
			opacity: 0;
			cursor: pointer;
		}
		&-file-uploader {
			width: 250px;
			height: 130px;
			position: absolute;
			opacity: 0;
			cursor: pointer;
		}
		&-title {
			display: flex;
			color: rgb(74, 74, 74);
			font-size: 14px;
			font-family: Montserrat, sans-serif;
			font-weight: 500;
			letter-spacing: -0.34px;
		}
		&-description {
			color: rgb(112, 128, 144);
			font-size: 12px;
			font-family: Montserrat, sans-serif;
			font-weight: normal;
			text-align: center;
			letter-spacing: -0.29px;
		}
		&-link {
			color: rgb(66, 160, 216);
			font-family: Montserrat, sans-serif;
			font-weight: bold;
			text-decoration: underline;
		}
		&-loader {
			height: 135px;
		}
	}
}

.v-input--reverse .v-input__slot {
	border: none !important;
	flex-direction: row-reverse;

	.v-application--is-ltr & {
		.v-input--selection-controls__input {
			margin-right: 0;
			margin-left: 8px;
		}
	}
	.v-application--is-rtl & {
		.v-input--selection-controls__input {
			margin-left: 0;
			margin-right: 8px;
		}
	}
}

.v-menu__content {
	width: 250px;
	background-color: white;

	.v-list-item {
		padding: 0 0 0 15px;
	}
}

.v-text-field__slot {
	input {
		padding: 10px 0 10px;
		font-size: 15px;
		font-weight: 300;
		max-height: 50px;
	}
}

.v-select__selection--comma {
	margin-left: 10px;
}
</style>
